import React from 'react';

/**
 * Skeleton do PagedList (REDLINE-loading.md §4). Reproduz o shell da tela:
 * header + filter panel + tabela shimmer.
 *
 * <p><b>Auto-contido de verdade:</b> inline styles + tokens globais + `.sk`
 * (LoadingSystem.css, eager). NÃO importa `DataTable` (que puxaria
 * react-bootstrap Table/Button/Dropdown + moment + useConfig — TUDO em chunk
 * lazy). Se importasse, o skeleton usado como Suspense fallback suspenderia
 * ao renderizar (aguardando o próprio chunk), e o outer Suspense (MainPage)
 * pegaria o suspend → aparecia Loader em vez do skeleton (bug clássico
 * "skeleton dependendo de chunk lazy" — 2026-08-02).
 *
 * <p>Antes usava classes `.dt-sk-head` / `.dt-sk-row` / `.dt-sk-foot` que
 * vivem em DataTable.css. Agora inline pra não depender do chunk do DataTable
 * ter chegado.
 */

// Larguras variadas por linha do skeleton — "fileira uniforme parece tabela
// vazia" (REDLINE §4). Valores em % — bandas do spec 24-38 / 15-22 / 10-13.
const SKELETON_ROWS = [
	{ a: 34, b: 18, c: 12 },
	{ a: 28, b: 22, c: 10 },
	{ a: 38, b: 15, c: 13 },
	{ a: 24, b: 20, c: 11 },
];

export default function PagedListSkeleton() {
	return (
		<div
			style={{
				background: 'var(--paper)',
				border: '1px solid var(--ink-100)',
				borderRadius: 13,
				overflow: 'hidden',
			}}
		>
			{/* Header (.pgl-phead) */}
			<div
				aria-hidden="true"
				style={{
					padding: '14px 20px',
					borderBottom: '1px solid var(--ink-100)',
					display: 'flex',
					alignItems: 'center',
					gap: 10,
					flexWrap: 'wrap',
				}}
			>
				<span className="sk" style={{ width: 140, height: 15 }} />
				<span style={{ flex: 1 }} />
				<span className="sk" style={{ width: 100, height: 32, borderRadius: 9 }} />
				<span className="sk" style={{ width: 100, height: 32, borderRadius: 9 }} />
			</div>

			{/* Filter panel (.pgl-fpanel) */}
			<div
				aria-hidden="true"
				style={{
					padding: '14px 20px',
					borderBottom: '1px solid var(--ink-100)',
					background: 'var(--ink-50)',
				}}
			>
				<div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
					<span className="sk" style={{ flex: '0 1 260px', height: 36, borderRadius: 9 }} />
					<span className="sk" style={{ width: 90, height: 36, borderRadius: 9 }} />
				</div>
			</div>

			{/* Table skeleton — inline (era DataTable isLoading) */}
			<div aria-busy="true" aria-live="polite">
				<div
					aria-hidden="true"
					style={{
						display: 'flex',
						gap: 16,
						padding: '11px 20px',
						borderBottom: '1px solid var(--ink-100)',
					}}
				>
					<span className="sk" style={{ width: 90, height: 9 }} />
					<span className="sk" style={{ width: 70, height: 9 }} />
					<span className="sk" style={{ width: 60, height: 9 }} />
					<span className="sk" style={{ width: 64, height: 9, marginLeft: 'auto' }} />
				</div>
				{SKELETON_ROWS.map((row, i) => {
					const isLast = i === SKELETON_ROWS.length - 1;
					return (
						<div
							key={i}
							aria-hidden="true"
							style={{
								display: 'flex',
								alignItems: 'center',
								gap: 14,
								padding: '12px 20px',
								borderBottom: isLast ? 'none' : '1px solid var(--ink-50)',
							}}
						>
							<span className="sk skc" style={{ width: 32, height: 32, flexShrink: 0 }} />
							<span className="sk" style={{ width: row.a + '%', height: 12 }} />
							<span className="sk" style={{ width: row.b + '%', height: 12 }} />
							<span className="sk" style={{ width: row.c + '%', height: 12 }} />
							<span className="sk" style={{ width: 56, height: 19, borderRadius: 999, marginLeft: 'auto' }} />
						</div>
					);
				})}
				<div
					aria-hidden="true"
					style={{
						display: 'flex',
						alignItems: 'center',
						gap: 10,
						padding: '12px 20px',
						background: 'var(--ink-50)',
						borderTop: '1px solid var(--ink-100)',
					}}
				>
					<span className="sk" style={{ width: 110, height: 10 }} />
					<span className="sk" style={{ width: 70, height: 10, marginLeft: 'auto' }} />
				</div>
			</div>
		</div>
	);
}
